import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { autocompleteClasses } from "@mui/material/Autocomplete";

const OVERFLOW_CHIP_WIDTH = 40;
const GAP = 3;

/**
 * Measures how many chips fit within a container's width,
 * reserving space for a "+N" overflow indicator when needed.
 *
 * Returns `containerRef` (attach to the flex container),
 * `visibleCount`, and `hiddenCount`.
 *
 * Chips inside the container must have `data-chip` attribute for measurement.
 */
export const useAutoFitChips = (
  totalCount: number,
  maxShownValues: number
): {
  containerRef: RefObject<HTMLDivElement | null>;
  visibleCount: number;
  hiddenCount: number;
} => {
  const autoFit = maxShownValues === -1;
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuring, setMeasuring] = useState(autoFit);
  const [visibleCount, setVisibleCount] = useState(totalCount);

  // Enter measuring mode when item count changes
  useLayoutEffect(() => {
    if (autoFit) setMeasuring(true);
  }, [autoFit, totalCount]);

  // Measure: runs when measuring=true, all items are rendered
  useLayoutEffect(() => {
    if (!autoFit || !measuring) return;

    const container = containerRef.current;
    if (!container) return;

    const chips = container.querySelectorAll<HTMLElement>("[data-chip]");
    if (!chips.length) return;

    const parent = container.parentElement;
    // Subtract the endAdornment (dropdown arrow + optional clear button) width so
    // chips don't render underneath it. The endAdornment is absolutely positioned
    // as a sibling, so it doesn't reduce clientWidth on its own.
    const endAdornment = parent?.querySelector<HTMLElement>(
      `.${autocompleteClasses.endAdornment}`
    );
    const reservedRight = endAdornment?.offsetWidth ?? 0;
    const containerWidth =
      (parent?.clientWidth ?? container.clientWidth) - reservedRight;
    let used = 0;
    let count = 0;

    for (let i = 0; i < chips.length; i++) {
      const w = chips[i].offsetWidth;
      const added = count > 0 ? GAP + w : w;
      const remaining = totalCount - count - 1;

      if (
        used + added + (remaining > 0 ? GAP + OVERFLOW_CHIP_WIDTH : 0) <=
        containerWidth
      ) {
        used += added;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(Math.max(1, count));
    setMeasuring(false);
  }, [autoFit, measuring, totalCount]);

  // Re-measure on resize — observe the parent element since our container's
  // own width doesn't grow when we show fewer chips (overflow: hidden)
  useEffect(() => {
    if (!autoFit) return;

    const container = containerRef.current;
    const observed = container?.parentElement ?? container;
    if (!observed) return;

    const ro = new ResizeObserver(() => setMeasuring(true));
    ro.observe(observed);

    return () => ro.disconnect();
  }, [autoFit]);

  const effective = autoFit
    ? measuring
      ? totalCount
      : visibleCount
    : maxShownValues;

  return {
    containerRef,
    visibleCount: effective,
    hiddenCount: Math.max(0, totalCount - effective),
  };
};
